import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosClient } from "@/lib/axiosClient";

export interface Period {
    period_id: number;
    name: string;
    start_date: string;
    end_date: string;
    start_date_payments?: string;
    end_date_payments?: string;
    is_active: boolean;
    card_background_front?: string | null;
    card_background_back?: string | null;
    card_background_front_url?: string | null;
    card_background_back_url?: string | null;
    qr_x?: number | null;
    qr_y?: number | null;
    qr_size?: number | null;
    text_x?: number | null;
    text_zone_top?: number | null;
    text_zone_bottom?: number | null;
    created_at?: string;
    updated_at?: string;
}

export type PeriodPayload = Omit<Period, "period_id" | "created_at" | "updated_at">;

// GET All Periods
export const useAdminPeriods = () => {
    return useQuery<Period[]>({
        queryKey: ["admin-periods"],
        queryFn: async () => {
            const { data } = await axiosClient.get("/periods", { withCredentials: true });
            return data;
        },
    });
};

// GET Single Period
export const useAdminPeriod = (id: number | null) => {
    return useQuery<Period>({
        queryKey: ["admin-period", id],
        queryFn: async () => {
            const { data } = await axiosClient.get(`/periods/${id}`, { withCredentials: true });
            return data;
        },
        enabled: !!id,
    });
};

// CREATE Period
export const useCreatePeriod = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload: PeriodPayload | FormData) => {
            // axiosClient set default header Content-Type: application/json, jadi harus di-override
            // manual saat body-nya FormData - kalau tidak, boundary multipart-nya tidak kebentuk dan
            // Laravel gagal parse file upload-nya (muncul error validasi "must be an image").
            const config = payload instanceof FormData
                ? { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } }
                : { withCredentials: true };
            const { data } = await axiosClient.post("/periods", payload, config);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-periods"] });
            queryClient.invalidateQueries({ queryKey: ["periods"] });
        },
    });
};

// UPDATE Period
// Menerima FormData (dipakai saat upload background kartu) - dikirim via POST + _method=PUT
// (spoofing standar Laravel) karena PHP tidak bisa parse file upload di request PUT asli.
export const useUpdatePeriod = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: number; payload: Partial<PeriodPayload> | FormData }) => {
            if (payload instanceof FormData) {
                payload.append("_method", "PUT");
                // Lihat catatan di useCreatePeriod soal kenapa Content-Type harus di-override manual.
                const { data } = await axiosClient.post(`/periods/${id}`, payload, {
                    withCredentials: true,
                    headers: { "Content-Type": "multipart/form-data" },
                });
                return data;
            }
            const { data } = await axiosClient.put(`/periods/${id}`, payload, { withCredentials: true });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-periods"] });
            queryClient.invalidateQueries({ queryKey: ["periods"] });
        },
    });
};

// HAPUS background kustom (front/back) - periode kembali pakai template default sistem.
export const useRemovePeriodCardBackground = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, side }: { id: number; side: "front" | "back" }) => {
            const { data } = await axiosClient.delete(`/periods/${id}/card-background/${side}`, { withCredentials: true });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-periods"] });
            queryClient.invalidateQueries({ queryKey: ["periods"] });
        },
    });
};

// DELETE Period
export const useDeletePeriod = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: number) => {
            const { data } = await axiosClient.delete(`/periods/${id}`, { withCredentials: true });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-periods"] });
            queryClient.invalidateQueries({ queryKey: ["periods"] });
        },
    });
};
